import { KeyboardEvent, useMemo, useState } from 'react';

import { getFullstoryProperties } from 'utils/property';
import { getAttachmentsOfType, updateAttachmentsOfType } from 'utils/survey';
import { Survey, SurveyQuestion } from 'types/survey';
import { IMAGE_CATEGORY, VIDEO_CATEGORY } from 'types/attachmentTypes';
import { Answer } from 'types/surveyResponse';
import { useSurveyTranslation } from 'hooks/useSurveyTranslation';
import { useSurveyQuestion } from './hooks';
import { TranslationsKeys, useTranslate } from 'hooks/useTranslate';
import { Images } from 'components/Images';
import { Modal } from 'components/Modal';
import { TextQuestion } from './components/TextQuestion';
import { RadioQuestion } from './components/RadioQuestion';
import { DropdownQuestion } from './components/DropdownQuestion';
import { SelectorQuestion } from './components/SelectorQuestion';
import { MultiselectQuestion } from './components/MultiselectQuestion';
import { CounterQuestion } from './components/CounterQuestion';
import { NumericQuestion } from './components/NumericQuestion';
import { NumericUnitsQuestion } from './components/NumericUnitsQuestion';
import styles from './Question.module.scss';

interface IProps {
  surveyDefinition: Survey;
  question: SurveyQuestion;
  answer: Answer;
  siblingAnswers: Answer[];
  disabled?: boolean;
  onChange: (answer: Answer) => void;
}

export const Question = ({ surveyDefinition, question, answer, siblingAnswers, disabled, onChange }: IProps): JSX.Element => {
  const ts = useSurveyTranslation(surveyDefinition);
  const { getSurveyQuestionDescription } = useSurveyQuestion(surveyDefinition);
  const description = getSurveyQuestionDescription(question);
  const [showLabel, setShowLabel] = useState<boolean>(true);
  const [showQuestionImage, setShowQuestionImage] = useState(false);
  const { t } = useTranslate();

  const input = useMemo((): JSX.Element => {
    switch (question.type) {
      case 'text':
        return (
          <TextQuestion surveyDefinition={surveyDefinition} question={question} answer={answer} onChange={onChange} />
        );

      case 'choice':
        switch (question.input) {
          case 'dropdown':
            setShowLabel(false);

            return (
              <DropdownQuestion
                surveyDefinition={surveyDefinition}
                question={question}
                answer={answer}
                siblingAnswers={siblingAnswers}
                onChange={onChange}
              />
            );

          case 'selector':
          case 'selector-fullwidth':
            return (
              <SelectorQuestion
                surveyDefinition={surveyDefinition}
                question={question}
                answer={answer}
                siblingAnswers={siblingAnswers}
                onChange={onChange}
              />
            );

          case 'multiselect':
          case 'multiselect-fullwidth':
            return (
              <MultiselectQuestion
                surveyDefinition={surveyDefinition}
                question={question}
                answer={answer}
                siblingAnswers={siblingAnswers}
                onChange={onChange}
              />
            );

          default:
            return (
              <RadioQuestion
                surveyDefinition={surveyDefinition}
                question={question}
                answer={answer}
                siblingAnswers={siblingAnswers}
                onChange={onChange}
              />
            );
        }

      case 'numeric':
        switch (question.input) {
          case 'counter':
            return <CounterQuestion question={question} answer={answer} onChange={onChange} />;

          case 'units':
            return (
              <NumericUnitsQuestion
                surveyDefinition={surveyDefinition}
                question={question}
                answer={answer}
                onChange={onChange}
              />
            );

          default:
            return <NumericQuestion question={question} answer={answer} onChange={onChange} />;
        }

      default:
        return <div className="text-danger">Unknown question type: {question.type}</div>;
    }
  }, [surveyDefinition, question, answer, siblingAnswers, onChange]);

  return (
    <div className={styles.question} {...getFullstoryProperties(question)}>
      {question.type !== 'attachment_only' && (
        <>
          {showLabel && (
            <label className={styles.questionLabel} htmlFor={'question-' + question.questionId}>
              {ts(question.name)}
            </label>
          )}
          {description && <p className={styles.questionDescription}>{description}</p>}
          {question.questionImage && (
            <p className={styles.questionDescription}>
              {question.questionImage.showHelperTextInQuestion && question.questionImage.helperText && (
                <div>{ts(question.questionImage.helperText)}</div>
              )}
              <button
                type="button"
                className={styles.questionImageLink}
                onClick={() => setShowQuestionImage(true)}
                onKeyUp={(event: KeyboardEvent) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    setShowQuestionImage(true);
                  }
                }}>
                {question.questionImage.linkText
                  ? ts(question.questionImage.linkText)
                  : t(TranslationsKeys.surveySectionPage_detailedDescription)}
              </button>
            </p>
          )}
          {disabled ? (
            <div className={styles.questionWrapper}>
              {input}
              <div className={styles.questionDisabledOverlay} />
            </div>
          ) : (
            input
          )}
        </>
      )}
      {question.allowImages && <div className={styles.questionImages}>
        <Images
          label={t(TranslationsKeys.images_images)}
          images={getAttachmentsOfType(answer.attachments, [IMAGE_CATEGORY, VIDEO_CATEGORY])}
          onImagesChanged={(images) =>
            onChange({
              ...answer,
              attachments: updateAttachmentsOfType(answer.attachments, [IMAGE_CATEGORY, VIDEO_CATEGORY], images)
            })
          }
        />
      </div>}
      {showQuestionImage && (
        <Modal
          title={ts(question.name)}
          secondaryButtonLabel={t(TranslationsKeys.common_close)}
          wide
          onSecondaryClick={() => setShowQuestionImage(false)}
          additionalContent={
            <>
              <div className={styles.questionImageWrapper}>
                <img className={styles.questionImage} alt="" src={question.questionImage!.url} />
              </div>
              {!question.questionImage!.showHelperTextInQuestion && question.questionImage!.helperText && (
                <p className={styles.questionImageHelperText}>{question.questionImage!.helperText}</p>
              )}
            </>
          }
        />
      )}
    </div>
  );
};
